import React from 'react';
import { FaShieldAlt, FaUserLock, FaDatabase, FaEye, FaHandshake, FaQuestionCircle } from 'react-icons/fa';

const PrivacyPolicy = () => {
  const sections = [
    {
      id: 'overview',
      title: 'Privacy Policy Overview',
      icon: <FaShieldAlt className="text-blue-500" />,
      content: `This Privacy Policy describes how Adira Logistics ("we," "our," or "us") collects, uses, and protects your information when you use our admin panel and related services. We are committed to protecting your privacy and ensuring the security of your personal information.`
    },
    {
      id: 'information-collection',
      title: 'Information We Collect',
      icon: <FaDatabase className="text-green-500" />,
      content: `We collect information you provide directly to us, such as when you create an account, log in, or use our services. This may include:
      
      • Personal identification information (name, email address, phone number)
      • Business information (company name, job title, department)
      • Account credentials and authentication data
      • Usage data and activity logs
      • Communication preferences and settings
      • Any other information you choose to provide`
    },
    {
      id: 'how-we-use',
      title: 'How We Use Your Information',
      icon: <FaEye className="text-purple-500" />,
      content: `We use the information we collect to:
      
      • Provide, maintain, and improve our services
      • Process and manage your account and access
      • Send you important updates and notifications
      • Respond to your requests and provide customer support
      • Ensure security and prevent fraud
      • Comply with legal obligations
      • Analyze usage patterns to enhance user experience`
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing and Disclosure',
      icon: <FaHandshake className="text-orange-500" />,
      content: `We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:
      
      • With your explicit permission
      • To comply with legal requirements or court orders
      • To protect our rights, property, or safety
      • In connection with a business transfer or merger
      • With service providers who assist in our operations (under strict confidentiality agreements)`
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: <FaUserLock className="text-red-500" />,
      content: `We implement appropriate technical and organizational security measures to protect your personal information against:
      
      • Unauthorized access or disclosure
      • Data loss or destruction
      • Alteration or corruption
      • Other security threats
      
      These measures include encryption, secure servers, access controls, and regular security assessments.`
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: <FaDatabase className="text-indigo-500" />,
      content: `We retain your personal information only for as long as necessary to:
      
      • Provide our services to you
      • Comply with legal obligations
      • Resolve disputes and enforce agreements
      • Maintain business records
      
      When we no longer need your information, we will securely delete or anonymize it.`
    },
    {
      id: 'your-rights',
      title: 'Your Rights and Choices',
      icon: <FaQuestionCircle className="text-teal-500" />,
      content: `You have the right to:
      
      • Access and review your personal information
      • Request corrections or updates to your data
      • Request deletion of your personal information
      • Opt-out of certain communications
      • Request data portability
      • Lodge a complaint with supervisory authorities
      
      To exercise these rights, please contact us using the information provided below.`
    },
    {
      id: 'data-deletion-requests',
      title: 'Data Deletion Requests',
      icon: <FaDatabase className="text-red-500" />,
      content: `You have the right to request the deletion of your personal information from our systems. Here's how to submit a data deletion request:

      **How to Submit a Deletion Request:**
      • Email: appsdesignservices@gmail.com (Subject: "Data Deletion Request")
      • Online Form: Available at our website
      
      **Required Information for Deletion Requests:**
      • Your full name and email address
      • Account ID or username (if applicable)
      • Specific data you want deleted
      • Reason for deletion (optional but helpful)
      
      **What Happens After Your Request:**
      • We will acknowledge your request within 48 hours
      • We will verify your identity to protect your privacy
      • We will process your request within 30 days
      • We will confirm when deletion is complete
      
      **What Gets Deleted:**
      • Personal identification information
      • Account credentials and settings
      • Usage data and activity logs
      • Communication preferences
      • Any other personal data we hold about you
      
      **Data We May Retain:**
      • Information required for legal compliance
      • Transaction records for tax purposes
      • Data needed for fraud prevention
      • Information required for ongoing legal proceedings
      
      **After Deletion:**
      • Your data will be permanently removed from our active systems
      • We will not be able to recover deleted information
      • You may need to create a new account if you wish to use our services again
      
      **Exceptions to Deletion:**
      We may not be able to delete certain information if:
      • It's required for legal compliance
      • It's needed for fraud prevention
      • It's part of ongoing legal proceedings
      • It's anonymized and no longer personally identifiable
      
      For questions about data deletion, contact our privacy team at privacy@adiralogistics.com`
    },
    {
      id: 'cookies-tracking',
      title: 'Cookies and Tracking Technologies',
      icon: <FaEye className="text-yellow-500" />,
      content: `We use cookies and similar tracking technologies to:
      
      • Remember your preferences and settings
      • Analyze how you use our services
      • Provide personalized content and features
      • Ensure security and prevent fraud
      
      You can control cookie settings through your browser preferences.`
    },
    {
      id: 'third-party-services',
      title: 'Third-Party Services',
      icon: <FaHandshake className="text-gray-500" />,
      content: `Our services may integrate with third-party services, such as:
      
      • Payment processors for billing
      • Analytics services for usage insights
      • Communication tools for notifications
      • Cloud storage providers for data backup
      
      These services have their own privacy policies, and we encourage you to review them.`
    },
    {
      id: 'children-privacy',
      title: 'Children\'s Privacy',
      icon: <FaShieldAlt className="text-pink-500" />,
      content: `Our services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us immediately so we can take appropriate action.`
    },
    {
      id: 'international-transfers',
      title: 'International Data Transfers',
      icon: <FaDatabase className="text-blue-600" />,
      content: `Your personal information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.`
    },
    {
      id: 'changes-policy',
      title: 'Changes to This Privacy Policy',
      icon: <FaShieldAlt className="text-green-600" />,
      content: `We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by:
      
      • Posting the updated policy on our website
      • Sending you an email notification
      • Displaying a notice in our admin panel
      
      Your continued use of our services after such changes constitutes acceptance of the updated policy.`
    },
    {
      id: 'contact-us',
      title: 'Contact Us',
      icon: <FaQuestionCircle className="text-blue-600" />,
      content: `If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
      
      **Adira Logistics**
      Email: privacy@adiralogistics.com
      Phone: +1-555-PRIVACY
      Address: 123 Privacy Street, Security City, SC 12345
      
      We will respond to your inquiry within 30 days.`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <FaShieldAlt className="text-3xl text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
          <div className="mt-4 text-sm text-gray-500">
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Table of Contents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section, index) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-blue-600 hover:text-blue-800 hover:underline flex items-center"
              >
                <span className="text-gray-500 mr-2">{index + 1}.</span>
                {section.title}
              </a>
            ))}
          </div>
        </div>

        {/* Privacy Policy Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={section.id} id={section.id} className="bg-white rounded-lg shadow-sm border p-8">
              <div className="flex items-center mb-6">
                <div className="mr-4">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">{section.title}</h2>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                  {section.content}
                </div>
              </div>
              
              {index < sections.length - 1 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <a
                    href={`#${sections[index + 1].id}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    Next: {sections[index + 1].title}
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <p className="text-gray-600 mb-4">
              Thank you for trusting Adira Logistics with your information.
            </p>
            <p className="text-sm text-gray-500">
              This privacy policy is effective as of the date shown above and applies to all users of our admin panel and related services.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

